import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Save } from "lucide-react";
import { HOMEPAGE_KEYS } from "@/clean/platform";
import { MAIN_HOME_DEFAULTS } from "@/pages/MainHomePage";
import { EVENT_HOME_DEFAULTS } from "@/game/GameHomePage";
import { FACILITATED_GAME_HOME_DEFAULTS } from "@/game/FacilitatedGamePage";
import { recruitmentHomeContent } from "@/content/siteContent";
import { GUIDED_PRODUCT_CONFIG } from "@/funnels/GuidedProductPages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });

const stripIcon = (value) => {
  const { icon, ...copy } = value || {};
  return copy;
};

const LOCAL_DEFAULTS = {
  main: MAIN_HOME_DEFAULTS,
  recruitment: recruitmentHomeContent,
  "strategic-planning": stripIcon(GUIDED_PRODUCT_CONFIG["strategic-planning"]),
  "board-recommitment": stripIcon(GUIDED_PRODUCT_CONFIG["board-recommitment"]),
  "facilitated-game": FACILITATED_GAME_HOME_DEFAULTS,
};

const prettyLabel = (value) => String(value)
  .replace(/_/g, " ")
  .replace(/([a-z])([A-Z])/g, "$1 $2")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const cloneContent = (value) => JSON.parse(JSON.stringify(value ?? {}));
const FUNDRAISING_HOME_FIELDS = new Set([
  "content_version", "event_home_version", "hero_badge", "headline", "subheadline", "hero_explanation",
  "cta_label", "stages_cta_label", "closing_cta_label", "start_supporting", "intro_heading", "intro_paragraphs",
  "stages_label", "stages_heading", "stages", "outcomes_label", "outcomes_heading", "outcomes",
  "testimonials_heading", "faqs_label", "faqs_heading", "faqs", "closing_heading", "closing_text",
]);

const setContentAtPath = (content, path, value) => {
  const next = cloneContent(content);
  let cursor = next;
  path.slice(0, -1).forEach((segment) => { cursor = cursor[segment]; });
  cursor[path[path.length - 1]] = value;
  return next;
};

const lockedTextKey = (key) => /(?:_url|_href|_route|_path|_link|_version)$/i.test(String(key));

const flattenContent = (value, path = [], label = "") => {
  const rows = [];
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      rows.push(...flattenContent(item, [...path, index], `${label || "Item"} ${index + 1}`));
    });
    return rows;
  }
  if (value && typeof value === "object") {
    Object.entries(value)
      .filter(([key]) => !lockedTextKey(key))
      .forEach(([key, item]) => {
        rows.push(...flattenContent(item, [...path, key], prettyLabel(key)));
      });
    return rows;
  }
  rows.push({ path, label, value });
  return rows;
};

function HomepageField({ row, onChange }) {
  const { path, label, value } = row;

  if (typeof value === "boolean") {
    return (
      <label className="terms-check" style={{ margin: "12px 0" }}>
        <input type="checkbox" checked={value} onChange={(event) => onChange(path, event.target.checked)} />
        <span>{label}</span>
      </label>
    );
  }

  if (typeof value === "number") {
    return (
      <label className="admin-notes">
        <strong>{label}</strong>
        <input type="number" value={value} onChange={(event) => onChange(path, Number(event.target.value))} />
      </label>
    );
  }

  const text = value == null ? "" : String(value);
  const long = text.length > 90 || /text|headline|heading|sub|promise|description|title|outcome|step|intro|note/i.test(label);

  return (
    <label className="admin-notes" style={{ margin: "12px 0" }}>
      <strong>{label}</strong>
      {long ? (
        <textarea
          aria-label={label}
          rows={Math.min(8, Math.max(2, Math.ceil(Math.max(text.length, 80) / 90)))}
          value={text}
          onChange={(event) => onChange(path, event.target.value)}
        />
      ) : (
        <input aria-label={label} value={text} onChange={(event) => onChange(path, event.target.value)} />
      )}
    </label>
  );
}

export function HomepageTextSection({ fixedPageKey = "" }) {
  const [pageKey, setPageKey] = useState(fixedPageKey || HOMEPAGE_KEYS[0][0]);
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async (key) => {
    setMessage("");
    try {
      const stored = (await axios.get(`${API}/platform/homepages/${key}`)).data.content || {};
      let defaults = LOCAL_DEFAULTS[key] || {};
      if (key === "board-fundraising-game") {
        defaults = (await axios.get(`${API}/game/content`)).data.content || {};
      }
      const current = key === "board-fundraising-game" && stored.content_version !== defaults.content_version ? {} : stored;
      const loaded = { ...cloneContent(defaults), ...(key === "board-fundraising-game" ? cloneContent(EVENT_HOME_DEFAULTS) : {}), ...cloneContent(current) };
      if (key === "board-fundraising-game") {
        if (current.event_home_version !== 1) Object.assign(loaded, EVENT_HOME_DEFAULTS);
        loaded.event_home_version = 1;
        Object.keys(loaded).forEach(field => { if (!FUNDRAISING_HOME_FIELDS.has(field)) delete loaded[field]; });
      }
      setContent(loaded);
    } catch (error) {
      setMessage(error.response?.data?.detail || "Could not load this home page text.");
    }
  }, []);

  useEffect(() => { load(pageKey); }, [pageKey, load]);

  const updateField = (path, value) => setContent((current) => setContentAtPath(current, path, value));

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      await client.put(`/admin/platform/homepages/${pageKey}`, { content });
      setMessage("Home page text saved.");
    } catch (error) {
      setMessage(error.response?.data?.detail || "Could not save this home page.");
    }
    setSaving(false);
  };

  const fields = flattenContent(content);

  return (
    <section data-testid="clean-homepage-editor">
      <div className="admin-funnel-numbers-head">
        <div>
          <h2>Home Page Text Edit</h2>
          <p>Edit your homepage text and event invitation buttons. Public homepage buttons always lead to the next live event.</p>
        </div>
      </div>
      <div className="admin-filters">
        {!fixedPageKey && <label>
          Home Page
          <select value={pageKey} onChange={(event) => setPageKey(event.target.value)}>
            {HOMEPAGE_KEYS.map(([key, name]) => <option key={key} value={key}>{name}</option>)}
          </select>
        </label>}
        <button className="button button-back button-small" onClick={() => load(pageKey)}><RefreshCw size={15} /> Reload Saved Text</button>
      </div>
      <div className="admin-homepage-text-fields">
        {fields.map((row, index) => (
          <HomepageField key={row.path.join(".") || index} row={row} onChange={updateField} />
        ))}
      </div>
      {message && <p className="admin-message">{message}</p>}
      <button className="button" disabled={saving} onClick={save}>
        <Save size={15} /> {saving ? "SAVING…" : "SAVE HOME PAGE TEXT"}
      </button>
    </section>
  );
}
